import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { FP_APP_URL } from '@/constants/Config';
import type { IProductSizeChartApiResponse } from '@/common/interfaces/size-chart.interface';

/**
 * Fetches normalized product size chart from FP.com (public; no credentials).
 * Used by cart Size chart modal — do not use axiosBaseQuery (withCredentials breaks CORS).
 */
export const productSizeChartQuery = createApi({
  reducerPath: 'productSizeChartQuery',
  baseQuery: fakeBaseQuery(),
  endpoints: (build) => ({
    getProductSizeChart: build.query<IProductSizeChartApiResponse, string>({
      queryFn: async (styleCode) => {
        const trimmed = styleCode?.trim();
        if (!trimmed) {
          return { data: { sizeChart: null } };
        }

        try {
          const url = `${FP_APP_URL}api/products/size-chart?styleCode=${encodeURIComponent(
            trimmed
          )}`;
          const response = await fetch(url, { method: 'GET' });

          if (response.status === 404) {
            return { data: { sizeChart: null } };
          }

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: await response.text().catch(() => 'Failed to load size chart'),
              },
            };
          }

          const body = (await response.json()) as IProductSizeChartApiResponse;
          return { data: { sizeChart: body?.sizeChart ?? null } };
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR' as const,
              error: error instanceof Error ? error.message : 'Failed to load size chart',
            },
          };
        }
      },
    }),
  }),
});

export const { useGetProductSizeChartQuery, useLazyGetProductSizeChartQuery } =
  productSizeChartQuery;
